import PieChartWidget from './pieChartWidget'
import { checkIfAllEmpty, getFormattedProcessesData } from './utils'
import {
  ACTION_FETCH_RISK_BASED_ON_GROUPS,
  RiskGroupCount,
  fetchRiskBasedOnGroups
} from '../privacyOpsOverviewSlice'
import { PiaRiskOption, fetchPiaRiskOptions } from '../../pia/piaSlice'
import { RootState } from '../../../rootReducer'
import { getShowLoader } from '../../../reducers/requestReducer'
import { getUpdatedUrl } from '../../../utils/urlUtil'
import {
  FILTER_REVIEW_SCHEDULE,
  FILTER_REVIEW_STATUS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  PiaProcessFilter,
  URL_DEFINED_PROCESSES,
  URL_PIA
} from '../../../constants'
import { useIntl } from 'react-intl'
import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Typography } from '@lightbeamai/design-system'

const MAP_PROCESS_GROUP_TO_COLORS = [
  '#5F175F',
  '#9F269F',
  '#CF35CF',
  '#DB67DB',
  '#E698E6',
  '#737D93'
]

interface IProps {
  risksBasedOnGroups: RiskGroupCount[]
  riskOptions?: PiaRiskOption[]
  totalPiaProcesses: number
  timePeriod?: string
  fetchRiskBasedOnGroups: (timePeriod: string) => void
  fetchPiaRiskOptions: () => void
  showLoader: boolean
}

const RisksBasedOnGroupsWidget = ({
  risksBasedOnGroups,
  riskOptions,
  totalPiaProcesses,
  timePeriod = '',
  fetchRiskBasedOnGroups,
  fetchPiaRiskOptions,
  showLoader
}: IProps) => {
  const intl = useIntl()

  useEffect(() => {
    fetchRiskBasedOnGroups(timePeriod)
  }, [fetchRiskBasedOnGroups, timePeriod])

  useEffect(() => {
    fetchPiaRiskOptions()
  }, [fetchPiaRiskOptions])

  const formattedData = useMemo(
    () =>
      getFormattedProcessesData(
        risksBasedOnGroups,
        totalPiaProcesses,
        URL_PIA,
        intl,
        MAP_PROCESS_GROUP_TO_COLORS,
        {
          [FILTER_REVIEW_SCHEDULE]: riskOptions?.map((item) => item.id).join(),
          [FILTER_REVIEW_STATUS]: PiaProcessFilter.reviewed
        }
      ),
    [risksBasedOnGroups, totalPiaProcesses, riskOptions, intl]
  )

  return (
    <PieChartWidget
      title={intl.formatMessage({ id: 'po.dashboard.riskBasedOnGroups.widget.title' })}
      help={
        <>
          {intl.formatMessage({ id: 'po.dashboard.riskBasedOnGroups.widget.helpText' })}
          <Typography variant={'baseMinus4'} color="secondary" as="i" className="block font-italic">
            {intl.formatMessage({ id: 'po.dashboard.riskBasedOnGroups.widget.infoText' })}
          </Typography>
        </>
      }
      data={checkIfAllEmpty(formattedData)}
      legendPosition="right"
      showLoader={showLoader}
      viewAllBtnText={intl.formatMessage({
        id: 'po.dashboard.riskBasedOnGroups.widget.viewAllBtn'
      })}
      viewAllLink={getUpdatedUrl({
        pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
        pageName: URL_DEFINED_PROCESSES,
        pageQueryParams: {
          [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
          [FILTER_REVIEW_STATUS]: PiaProcessFilter.reviewed
        }
      })}
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  risksBasedOnGroups: state.privacyOpsOverview.risksBasedOnGroups,
  riskOptions: state.pia.riskOptions,
  totalPiaProcesses: state.privacyOpsOverview.totalPiaProcesses,
  timePeriod: state.customDashboards.filterProps.timePeriod,
  showLoader: getShowLoader(ACTION_FETCH_RISK_BASED_ON_GROUPS)
})

const mapDispatchToProps = {
  fetchRiskBasedOnGroups,
  fetchPiaRiskOptions
}

export default connect(mapStateToProps, mapDispatchToProps)(RisksBasedOnGroupsWidget)
